import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Confetti from "react-confetti";
import api, { formatApiError } from "@/lib/api";
import { TRIBES, SYMPTOM_OPTIONS } from "@/lib/tribes";
import CheckInForm from "@/components/app/CheckInForm";
import EducationModule from "@/components/app/EducationModule";
import VideoEmbed from "@/components/app/VideoEmbed";
import { useAuth } from "@/context/AuthContext";
import { useOverride } from "@/context/ContentContext";
import { EDUCATION_MODULES } from "@/data/educationModules";
import { BREATHE_FRAMEWORK } from "@/data/breatheFramework";
import { Check, ArrowLeft, ArrowRight, Video, Sparkles, AlertCircle, Eye, FastForward, Wind, X, Download, Printer } from "lucide-react";

// Kajabi add-on checkout — placeholder fallback if the admin hasn't set the real URL
// yet under Admin > Bulk Videos > Support links > "Support — Add-on Family Slot Checkout URL".
const ADDON_CHECKOUT_FALLBACK = "https://YOUR-KAJABI-ADDON-CHECKOUT-URL";

// Steps: 0 child · 1 terms & medical disclaimer · 2 tribe-intro · 3 tribe-explore · 4 tribe-detail
//        · 5 induction-manifesto · 6 induction-welcome · 7 appliance · 8 symptoms-pick · 9 symptoms-learn-loop
//        · 10 breathe-popup · 11 goals-and-terms module · 12 baseline-video · 13 baseline-checkin
const STEP_TERMS = 1;
const STEP_TRIBE_INTRO = 2;
const STEP_TRIBE_EXPLORE = 3, STEP_TRIBE_DETAIL = 4, STEP_INDUCTION_MANIFESTO = 5, STEP_INDUCTION_WELCOME = 6;
const STEP_APPLIANCE = 7, STEP_SYMPTOMS = 8, STEP_SYMPTOM_LEARN = 9;
const STEP_BREATHE = 10, STEP_EDU_GOALS = 11;
const STEP_BASELINE_VIDEO = 12, STEP_BASELINE_FORM = 13;
const TOTAL_STEPS = 14;

// Terms text now lives on a dedicated public page at /terms (see Terms.jsx). The enrollment
// checkbox links out to that page in a new tab so parents can read (and print) the full doc
// without losing their in-progress enrollment state. Admin CMS still stores the copy under
// scope=policy, subject=terms_and_medical_disclaimer, field=text — Terms.jsx reads that same
// override on load.

// Baseline "How to observe your child" video — pulled from the CMS via
// content_type=baseline_observation, key=main, field=video_url so an admin can update the
// therapist's guidance video without a code push. Wrapped in its own component so the
// useOverride hook runs only when this step is actually rendered.
function BaselineVideoStep({ child, onNext }) {
  const url = useOverride("baseline_observation", "main", "video_url", "");
  return (
    <div className="bg-warm-white border border-edge rounded-3xl p-8 md:p-12" data-testid="baseline-video">
      <div className="text-xs uppercase tracking-widest text-mauve font-semibold">Before your first check-in</div>
      <h1 className="font-display text-3xl font-bold text-navy mt-1">How to observe {child.name}</h1>
      <p className="text-ink-mid mt-2">A short guide from the therapist teaching you exactly what to notice — nasal vs mouth breathing, lip seal, tongue rest position, and swallow. Watch first, then take the baseline honestly.</p>
      <div className="mt-6" data-testid="baseline-video-embed">
        <VideoEmbed value={url} title="How to observe your child" placeholder="Educational video — how to observe your child" testid="baseline-video-embed" />
      </div>
      <div className="mt-6 rounded-2xl bg-mauve-pale border border-mauve p-4 text-sm text-ink-dark">
        If you're not sure about an answer during the check-in, that's fine — every question has an <b>"I need more time to observe"</b> option. You can save your draft and come back in 24 hours.
      </div>
      <div className="mt-8 flex justify-end">
        <button data-testid="baseline-video-next" onClick={onNext} className="bg-gold text-ink-dark px-6 py-3 rounded-full font-semibold hover:bg-gold-light">I've watched — start the check-in</button>
      </div>
    </div>
  );
}

function TribeVideo({ tribe }) {
  const url = useOverride("tribe", tribe.key, "video_url", "");
  return (
    <VideoEmbed
      value={url}
      title={`${tribe.name} welcome`}
      placeholder={`Welcome video — ${tribe.name}`}
      theme={{ borderColor: tribe.accent, background: tribe.panel_soft, color: tribe.ink }}
      testid={`tribe-video-${tribe.key}`}
    />
  );
}

function TribeText({ tribeKey, field, fallback }) {
  const v = useOverride("tribe", tribeKey, field, fallback);
  return <>{v}</>;
}

function TribeIntroStep({ onBack, onContinue }) {
  const url = useOverride("tribe_intro", "main", "video_url", "");
  return (
    <div className="bg-warm-white border border-edge rounded-3xl p-8 md:p-12" data-testid="tribe-intro">
      <div className="text-xs uppercase tracking-widest text-mauve font-semibold">Before you choose</div>
      <h1 className="font-display text-3xl font-bold text-navy mt-1">What is a Tribe?</h1>
      <p className="text-ink-mid mt-2 max-w-2xl">A short intro before you meet the three tribes.</p>
      <div className="mt-6" data-testid="tribe-intro-video">
        <VideoEmbed
          value={url}
          title="What is a Tribe?"
          placeholder="What is a Tribe? — intro video coming soon"
          testid="tribe-intro-video"
        />
      </div>
      <div className="mt-8 flex justify-between">
        <button onClick={onBack} data-testid="tribe-intro-back" className="text-ink-mid px-4 py-3">Back</button>
        <button
          onClick={onContinue}
          data-testid="tribe-intro-continue"
          className="bg-gold text-ink-dark px-6 py-3 rounded-full font-semibold hover:bg-gold-light hover:-translate-y-0.5 transition-all"
        >
          Continue
        </button>
      </div>
    </div>
  );
}

function ManifestoStep({ tribe, onBack, onContinue }) {
  const pdfUrl = useOverride("tribe", tribe.key, "manifesto_pdf_url", tribe.manifesto_pdf || "");
  const imageUrl = useOverride("tribe", tribe.key, "manifesto_image_url", tribe.manifesto_image || "");

  const doPrint = () => {
    // Print the illustrated PDF via a hidden window so the browser's own print
    // dialog handles pagination and quality (better than printing a rasterized
    // PNG at screen resolution).
    if (!pdfUrl) return;
    const win = window.open(pdfUrl, "_blank", "noopener,noreferrer");
    if (!win) return;
    // Give the tab a beat to render before invoking print; some browsers
    // silently ignore auto-print, in which case the user still has the tab.
    try {
      win.addEventListener("load", () => { try { win.focus(); win.print(); } catch (_) {} });
    } catch (_) {}
  };

  return (
    <div
      className="rounded-3xl overflow-hidden"
      style={{ background: tribe.key === "nasal_ninja" ? tribe.bg : tribe.surface, border: `2px solid ${tribe.accent}` }}
      data-testid="induction-manifesto"
    >
      <div className="p-6 md:p-10" style={{ color: tribe.ink }}>
        <div className="text-center">
          <div className="text-xs uppercase tracking-[0.4em] font-bold" style={{ color: tribe.accent }}>The {tribe.name} Manifesto</div>
          <div className="mt-2 text-xs italic opacity-70">Place your hand over your heart and read it aloud — then keep a copy.</div>
        </div>

        {imageUrl ? (
          <div className="mt-6">
            <div className="rounded-2xl overflow-hidden shadow-lg mx-auto" style={{ border: `1px solid ${tribe.accent}40`, background: "#FFFEF9", maxWidth: "720px" }} data-testid="manifesto-visual">
              <img
                src={imageUrl}
                alt={`${tribe.name} illustrated manifesto`}
                style={{ display: "block", width: "100%", height: "auto" }}
                draggable={false}
              />
            </div>
            {pdfUrl && (
              <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                <a
                  href={pdfUrl}
                  download={`${tribe.name.replace(/\s+/g, "-").toLowerCase()}-manifesto.pdf`}
                  data-testid="manifesto-download"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border-2 hover:-translate-y-0.5 transition-all"
                  style={{ borderColor: tribe.accent, color: tribe.ink, background: tribe.panel_soft }}
                >
                  <Download size={14} /> Download
                </a>
                <button
                  type="button"
                  onClick={doPrint}
                  data-testid="manifesto-print"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border-2 hover:-translate-y-0.5 transition-all"
                  style={{ borderColor: tribe.accent, color: tribe.ink, background: tribe.panel_soft }}
                >
                  <Printer size={14} /> Print
                </button>
              </div>
            )}
          </div>
        ) : (
          // Text-manifesto fallback for tribes that haven't uploaded illustrated art yet.
          <div className="mt-8 font-display text-lg md:text-xl leading-relaxed italic max-w-2xl mx-auto whitespace-pre-line text-center">
            "<TribeText tribeKey={tribe.key} field="manifesto" fallback={tribe.manifesto} />"
          </div>
        )}

        <div className="mt-8 flex items-center justify-between gap-3 flex-wrap">
          {onBack ? (
            <button type="button" onClick={onBack} data-testid="induction-manifesto-back" className="text-sm px-4 py-2 rounded-full" style={{ color: tribe.ink, opacity: 0.7 }}>
              Back
            </button>
          ) : <span />}
          <button
            type="button"
            data-testid="induction-manifesto-next"
            onClick={onContinue}
            className="px-6 py-3 rounded-full font-semibold hover:-translate-y-0.5 transition-all"
            style={{ background: tribe.accent, color: "#1E1E2E" }}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}

// Confetti-topped welcome that fires the instant the child crosses into the tribe.
// The next-steps checklist was retired here — we want this screen to feel like a
// celebration, not another to-do list. The details resurface later in enrollment.
function WelcomeStep({ tribe, name, onContinue }) {
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [runConfetti, setRunConfetti] = useState(true);

  useEffect(() => {
    const upd = () => setSize({ w: window.innerWidth, h: window.innerHeight });
    upd();
    window.addEventListener("resize", upd);
    // Stop spawning new pieces after 6s so the celebration doesn't loop; existing
    // pieces still fall to the bottom under gravity for a natural finish.
    const t = setTimeout(() => setRunConfetti(false), 6000);
    return () => { window.removeEventListener("resize", upd); clearTimeout(t); };
  }, []);

  return (
    <>
      <div className="fixed inset-0 pointer-events-none z-40" aria-hidden="true" data-testid="welcome-confetti">
        <Confetti
          width={size.w}
          height={size.h}
          colors={[tribe.accent, tribe.subtle, tribe.spark, "#FFFEF9", "#FF3CB4"]}
          numberOfPieces={runConfetti ? 260 : 0}
          recycle={false}
          gravity={0.22}
          tweenDuration={5200}
        />
      </div>
      <div
        className="rounded-3xl overflow-hidden"
        style={{ background: tribe.key === "nasal_ninja" ? tribe.bg : tribe.surface, border: `2px solid ${tribe.accent}` }}
        data-testid="induction-welcome"
      >
        <div className="p-8 md:p-14 text-center" style={{ color: tribe.ink }}>
          <Sparkles size={40} className="mx-auto animate-pulse" style={{ color: tribe.accent }} />
          <div className="mt-4 text-xs uppercase tracking-[0.4em] font-bold" style={{ color: tribe.accent }}>Welcome</div>
          <h2 className="font-display text-3xl md:text-5xl font-bold mt-4" style={{ color: tribe.ink }}>
            Congratulations, {name}!
          </h2>
          <div className="mt-4 text-lg" style={{ color: tribe.ink }}>
            You're part of the <span className="font-semibold" style={{ color: tribe.accent }}>{tribe.name}</span>.
          </div>
          <button
            data-testid="induction-welcome-next"
            onClick={onContinue}
            className="mt-10 px-6 py-3 rounded-full font-semibold hover:-translate-y-0.5 transition-all"
            style={{ background: tribe.accent, color: "#1E1E2E" }}
          >
            Continue enrollment
          </button>
        </div>
      </div>
    </>
  );
}

export default function Enrollment() {
  const nav = useNavigate();
  const { user, refreshUser } = useAuth();
  // Feature flag: hide the "Free preview" option from new-parent enrollment. Left in the
  // code (backend endpoints + admin downgrade + PreviewGate banner) so existing preview
  // users still see their countdown, and so we can flip this to `true` later without a
  // code migration. Turned OFF at launch to avoid recent Kajabi purchasers second-guessing
  // themselves into a free trial after they've already paid.
  const PREVIEW_ENROLLMENT_ENABLED = false;
  const addonCheckoutUrl = useOverride("support", "addon_checkout", "link_url", ADDON_CHECKOUT_FALLBACK);
  const educationAlreadyCompleted = !!(user && user.education_completed_at);
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState("child"); // "child" | "self" | "preview"
  const isSelf = mode === "self";
  const isPreview = PREVIEW_ENROLLMENT_ENABLED && mode === "preview";
  const [name, setName] = useState("");
  const [age, setAge] = useState("");
  const [exploringTribe, setExploringTribe] = useState(null);
  const [tribe, setTribe] = useState("");
  const [appliance, setAppliance] = useState(null);
  const [symptoms, setSymptoms] = useState([]);
  const [symptomsData, setSymptomsData] = useState([]);
  const [symptomIdx, setSymptomIdx] = useState(0);
  const [symptomStage, setSymptomStage] = useState("ask"); // ask | safety | baseline
  const [symptomBaselines, setSymptomBaselines] = useState({});
  const [child, setChild] = useState(null);
  const [error, setError] = useState("");
  const [slotBlock, setSlotBlock] = useState(null); // {message, checkout_url} when 402 slot_limit_reached
  const [saving, setSaving] = useState(false);
  const [eduViewed, setEduViewed] = useState({ goals_terms: false });
  const [termsAccepted, setTermsAccepted] = useState(!!(user && user.terms_accepted_at));
  const [tsUsed, setTsUsed] = useState(0);

  useEffect(() => { api.get("/symptoms").then((r) => setSymptomsData(r.data)); }, []);
  // Fetch how many Thrive Story children the parent has already enrolled, so we can
  // decide whether the 5th card renders as "included" (free slot available) or
  // "add-on" (needs a sibling purchase).
  useEffect(() => {
    if (!user?.has_thrive_story_bonus) return;
    api.get("/children").then((r) => {
      setTsUsed((r.data || []).filter((c) => c.tribe === "thrive_story").length);
    }).catch(() => {});
  }, [user?.has_thrive_story_bonus]);
  const tsAllowed = (user?.has_thrive_story_bonus ? 1 : 0) + Number(user?.thrive_story_paid_slots || 0);
  const tsHasFreeSlot = user?.has_thrive_story_bonus && tsUsed < tsAllowed;
  const tsNeedsAddon = user?.has_thrive_story_bonus && !tsHasFreeSlot;

  // Note: no pre-flight "slot limit" check here. The modal used to fire on mount when the
  // parent already had children, but that also fired erroneously mid-enrollment because
  // refreshUser() (called after each education completion) mutates `user` and re-triggers
  // the effect. Instead, the modal only fires reactively when POST /children returns 402
  // with code=slot_limit_reached — i.e. when they actually try to enroll another child,
  // never pre-emptively during a first-time flow.

  const toggleSymptom = (k) => setSymptoms((s) => s.includes(k) ? s.filter((x) => x !== k) : [...s, k]);

  const createChild = async () => {
    setError(""); setSlotBlock(null); setSaving(true);
    try {
      if (isPreview) {
        try { await api.post("/auth/preview-start"); await refreshUser?.(); } catch (_) {}
      }
      const payload = {
        name,
        age: age === "" ? null : parseInt(age, 10),
        tribe: isSelf ? "neutral" : tribe,
        appliance_in_use: appliance,
        top_symptoms: symptoms,
        is_self: isSelf,
        symptom_baselines: symptomBaselines,
      };
      const { data } = await api.post("/children", payload);
      setChild(data);
      setStep(STEP_BREATHE);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 402 && d?.code === "slot_limit_reached") {
        setSlotBlock({ message: d.message || "You're all set up to your current enrollment.", checkout_url: d.checkout_url || "" });
      } else {
        setError(formatApiError(e));
      }
    }
    finally { setSaving(false); }
  };

  const onBaselineDone = () => nav(`/child/${child.id}`);

  const currentSymptomKey = symptoms[symptomIdx];
  const currentSymptomData = symptomsData.find((s) => s.key === currentSymptomKey);
  const isMergedPalate = currentSymptomKey === "crooked_teeth_narrow_palate";
  const mergedFallback = isMergedPalate
    ? {
        key: "crooked_teeth_narrow_palate",
        name: "Crooked/Crowded Teeth/Narrow Palate",
        description: "Two closely-linked signs of underdeveloped jaw and airway. Low tongue posture and mouth breathing narrow the palate and crowd teeth. Palate width is worth measuring — see the walkthrough for a home measurement using the red wax strip.",
        safety_note: "Share progress at your next dental visit — tongue posture work often supports orthodontic outcomes. A high, narrow palate is also worth flagging to your dentist or orthodontist.",
        referral: null,
      }
    : null;
  const activeSymptomData = currentSymptomData || mergedFallback;
  const mergedParts = isMergedPalate
    ? [
        symptomsData.find((s) => s.key === "crooked_teeth"),
        symptomsData.find((s) => s.key === "narrow_palate"),
      ].filter(Boolean)
    : [];

  const advanceSymptomLoop = () => {
    if (symptomIdx + 1 < symptoms.length) {
      setSymptomIdx((i) => i + 1);
      setSymptomStage("ask");
    } else {
      createChild();
    }
  };

  const finishSymptomsThenEducation = async () => {
    // Save child now if not yet, then move to BREATHE popup
    if (!child) { await createChild(true); } else { setStep(STEP_BREATHE); }
  };

  const tribeExploring = exploringTribe && TRIBES[exploringTribe];

  // Slot-limit modal — renders as an overlay on top of the current enrollment step so the
  // parent gets a clear explanation the moment they open /enroll (or when 402 fires mid-flow).
  const SlotBlockModal = slotBlock ? (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="slot-block-modal">
      <div className="absolute inset-0 bg-navy/40 backdrop-blur-sm" onClick={() => nav("/dashboard")} />
      <div className="relative bg-warm-white border-2 border-mauve rounded-3xl p-8 md:p-10 max-w-lg w-full shadow-[0_30px_80px_rgba(44,45,91,0.25)]">
        <button
          onClick={() => nav("/dashboard")}
          data-testid="slot-block-close"
          className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-ink-soft hover:bg-cream"
          aria-label="Close"
        >
          <X size={16} />
        </button>
        <div className="w-14 h-14 rounded-2xl bg-mauve text-warm-white flex items-center justify-center">
          <Sparkles size={22} />
        </div>
        <h2 className="font-display text-2xl font-bold text-navy mt-5">You have zero enrollment spots left</h2>
        <div className="mt-4 space-y-3 text-sm text-ink-mid leading-relaxed">
          <p>{slotBlock.message}</p>
          <div className="rounded-xl bg-cream border border-edge px-4 py-3 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-ink-soft">Paid spots</span>
              <span className="font-semibold text-navy">{slotBlock.paid_slots ?? 0}</span>
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-ink-soft">Currently enrolled</span>
              <span className="font-semibold text-navy">{slotBlock.current_children ?? 0}</span>
            </div>
          </div>
          <p className="text-xs text-ink-soft">Each family member gets their own tribe, progress, and certificate. You can add more anytime.</p>
        </div>
        <div className="mt-7 flex flex-col sm:flex-row gap-3">
          <a
            href={slotBlock.checkout_url || addonCheckoutUrl || ADDON_CHECKOUT_FALLBACK}
            target="_blank"
            rel="noreferrer"
            data-testid="slot-block-checkout"
            className="flex-1 inline-flex items-center justify-center gap-2 bg-gold text-ink-dark px-5 py-3 rounded-full font-semibold hover:bg-gold-light"
          >
            <Sparkles size={14} /> Purchase more here
          </a>
          <button
            onClick={() => nav("/dashboard")}
            data-testid="slot-block-back"
            className="inline-flex items-center justify-center gap-2 border border-edge text-navy px-5 py-3 rounded-full font-semibold hover:bg-cream"
          >
            Back to dashboard
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div className="min-h-screen shell-bg py-10 px-4">
      {SlotBlockModal}
      <div className="max-w-3xl mx-auto">
        <div className="mb-8 flex items-center gap-2">
          {Array.from({ length: TOTAL_STEPS }, (_, i) => i).map((s) => (
            <div key={s} className={`h-1.5 flex-1 rounded-full ${s <= step ? "bg-gold" : "bg-cream-dark"}`} />
          ))}
        </div>

        {step === 0 && (
          <div className="bg-warm-white border border-edge rounded-3xl p-8 md:p-12">
            <div className="text-xs uppercase tracking-widest text-mauve font-semibold">First things first</div>
            <h1 className="font-display text-3xl font-bold text-navy mt-1">Who is this program for?</h1>
            <p className="text-ink-mid mt-2">You can add more people later — each with their own journey and progress.</p>

            <div className={`mt-6 grid gap-3 ${PREVIEW_ENROLLMENT_ENABLED ? "sm:grid-cols-3" : "sm:grid-cols-2"}`} data-testid="enroll-mode-picker">
              <button
                type="button"
                data-testid="enroll-mode-child"
                onClick={() => setMode("child")}
                className={`text-left p-5 rounded-2xl border-2 transition-all ${mode === "child" ? "border-gold bg-cream" : "border-edge hover:border-gold-light"}`}
              >
                <div className="font-display font-bold text-navy">I'm enrolling my child</div>
                <div className="text-xs text-ink-mid mt-1">The full family program — tribe, manifesto, and everything else.</div>
              </button>
              <button
                type="button"
                data-testid="enroll-mode-self"
                onClick={() => setMode("self")}
                className={`text-left p-5 rounded-2xl border-2 transition-all ${mode === "self" ? "border-gold bg-cream" : "border-edge hover:border-gold-light"}`}
              >
                <div className="font-display font-bold text-navy">I'm doing this for myself (adult)</div>
                <div className="text-xs text-ink-mid mt-1">Same 12-session program, no tribe or kid-facing screens.</div>
              </button>
              {PREVIEW_ENROLLMENT_ENABLED && (
                <button
                  type="button"
                  data-testid="enroll-mode-preview"
                  onClick={() => setMode("preview")}
                  className={`text-left p-5 rounded-2xl border-2 transition-all ${mode === "preview" ? "border-mauve bg-mauve-pale" : "border-dashed border-edge hover:border-mauve"}`}
                >
                  <div className="font-display font-bold text-navy">Free preview</div>
                  <div className="text-xs text-ink-mid mt-1">Look around for 4 days — tribe, symptoms, dashboard. Sessions and check-ins unlock at enrollment.</div>
                </button>
              )}
            </div>

            <div className="mt-8 space-y-5">
              <div>
                <label className="text-xs uppercase tracking-widest text-ink-soft font-semibold">
                  {isSelf ? "Your name" : "Child's name"}
                </label>
                <input data-testid="enroll-child-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full px-4 py-3 rounded-xl border border-edge bg-cream focus:outline-none focus:ring-2 focus:ring-gold text-ink-dark" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-ink-soft font-semibold">
                  Age {isSelf && <span className="text-ink-soft/60 normal-case tracking-normal ml-1">(optional)</span>}
                </label>
                <input
                  data-testid="enroll-child-age"
                  type="number"
                  min={isSelf ? "1" : "3"}
                  max={isSelf ? "120" : "17"}
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  className="mt-1 w-full px-4 py-3 rounded-xl border border-edge bg-cream focus:outline-none focus:ring-2 focus:ring-gold text-ink-dark"
                />
              </div>
            </div>
            <div className="mt-8 flex justify-end">
              <button
                data-testid="enroll-step0-next"
                disabled={!name || (!isSelf && !age)}
                onClick={() => setStep(STEP_TERMS)}
                className="bg-gold text-ink-dark px-6 py-3 rounded-full font-semibold hover:bg-gold-light disabled:opacity-50"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {step === STEP_TERMS && (
          <div className="bg-warm-white border border-edge rounded-3xl p-8 md:p-12" data-testid="enroll-terms-step">
            <div className="text-xs uppercase tracking-widest text-mauve font-semibold">Before we go further</div>
            <h1 className="font-display text-3xl font-bold text-navy mt-1">Terms &amp; Medical Disclaimer</h1>
            <p className="text-ink-mid mt-2">
              A quick read that keeps everyone on the same page about what this program is (and isn't).
              {isSelf ? " Please review before we build your journey." : ` Please review before we set up ${name || "your child"}'s journey.`}
            </p>

            <label
              htmlFor="terms-accept-checkbox"
              className="mt-8 flex items-start gap-3 p-5 rounded-2xl border-2 cursor-pointer transition-all"
              style={{ borderColor: termsAccepted ? "#C9A96E" : "#E0D8E8", background: termsAccepted ? "#FBF6ED" : "#FFFEF9" }}
              data-testid="enroll-terms-checkbox-row"
            >
              <input
                id="terms-accept-checkbox"
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                data-testid="enroll-terms-checkbox"
                className="mt-1 h-4 w-4 accent-[#C9A96E] cursor-pointer"
              />
              <span className="text-sm text-ink-dark leading-relaxed">
                I have read and agree to the{" "}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="enroll-terms-link"
                  className="underline font-semibold text-navy hover:text-mauve"
                >
                  Terms of Use and Medical Disclaimer
                </a>
                .
              </span>
            </label>

            <div className="mt-8 flex justify-between items-center">
              <button type="button" onClick={() => setStep(0)} data-testid="enroll-terms-back" className="text-ink-mid px-4 py-3">Back</button>
              <button
                type="button"
                data-testid="enroll-terms-next"
                disabled={!termsAccepted}
                onClick={async () => {
                  // Persist acceptance to the user record so the timestamp is available for audits.
                  // Best-effort — if the endpoint is unavailable we still let them proceed rather
                  // than block enrollment on a network hiccup; the checkbox state carries through.
                  try {
                    await api.post("/users/me/accept-terms", { document: "terms_and_medical_disclaimer" });
                    refreshUser && refreshUser();
                  } catch (_) { /* non-blocking */ }
                  setStep(isSelf ? STEP_APPLIANCE : STEP_TRIBE_INTRO);
                }}
                className="bg-gold text-ink-dark px-6 py-3 rounded-full font-semibold hover:bg-gold-light disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {step === STEP_TRIBE_INTRO && (
          <TribeIntroStep
            onBack={() => setStep(0)}
            onContinue={() => setStep(STEP_TRIBE_EXPLORE)}
          />
        )}

        {step === STEP_TRIBE_EXPLORE && (
          <div className="bg-warm-white border border-edge rounded-3xl p-8 md:p-12">
            <div className="text-xs uppercase tracking-widest text-mauve font-semibold">Meet the tribes</div>
            <h1 className="font-display text-3xl font-bold text-navy mt-1">Explore all four before choosing</h1>
            <p className="text-ink-mid mt-2">Each tribe has its own welcome video and manifesto. Tap any card to explore — you can come back and try another.</p>
            <div className="mt-8 grid gap-4">
              {Object.values(TRIBES).filter((t) => t.key !== "neutral" && t.key !== "thrive_story").map((t) => (
                <button key={t.key} data-testid={`tribe-explore-${t.key}`} onClick={() => { setExploringTribe(t.key); setStep(STEP_TRIBE_DETAIL); }} className="text-left p-6 rounded-2xl border-2 border-edge hover:border-gold hover:-translate-y-0.5 transition-all">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-widest font-bold" style={{ color: t.subtle }}>Tribe</div>
                      <div className="font-display font-bold text-navy text-xl mt-1">{t.name}</div>
                    </div>
                    <ArrowRight className="text-gold" />
                  </div>
                  <p className="mt-3 text-sm text-ink-mid italic leading-relaxed line-clamp-2">"{t.manifesto}"</p>
                </button>
              ))}
              {user?.has_thrive_story_bonus && (
                <button
                  key="thrive_story"
                  type="button"
                  data-testid="tribe-explore-thrive_story"
                  onClick={() => {
                    if (tsNeedsAddon) {
                      // No free slot left — bounce to the Kajabi add-on checkout; the
                      // webhook will increment thrive_story_paid_slots on purchase.
                      window.open(addonCheckoutUrl || ADDON_CHECKOUT_FALLBACK, "_blank", "noopener,noreferrer");
                      return;
                    }
                    setTribe("thrive_story");
                    setStep(STEP_INDUCTION_WELCOME);
                  }}
                  className="text-left p-6 rounded-2xl border-2 transition-all hover:-translate-y-0.5"
                  style={{ borderColor: tsNeedsAddon ? "#C9A96E" : "#8B5E7A", background: "linear-gradient(135deg,#FBF5FA 0%,#FFFEF9 100%)" }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-widest font-bold" style={{ color: tsNeedsAddon ? "#C9A96E" : "#8B5E7A" }}>
                        {tsNeedsAddon ? "Add-on · for this sibling" : "Bonus · included"}
                      </div>
                      <div className="font-display font-bold text-navy text-xl mt-1">My Thrive Story</div>
                    </div>
                    <Sparkles className="text-mauve" />
                  </div>
                  <p className="mt-3 text-sm text-ink-mid italic leading-relaxed">
                    {tsNeedsAddon
                      ? `You've already used your Thrive Story bonus. Tap to add Thrive Story to this sibling — opens checkout in a new tab, and your card unlocks the moment the purchase is confirmed.`
                      : `A one-of-a-kind journey built just for your child — their favorite color, their adventure companion, and personalized certificates for every session. You'll fill out a short form after enrollment so we can craft it.`}
                  </p>
                  {tsNeedsAddon && (
                    <div className="mt-3 inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full border" style={{ borderColor: "#C9A96E", color: "#8B5E7A" }}>
                      Add Thrive Story to this sibling →
                    </div>
                  )}
                </button>
              )}
              <button
                type="button"
                data-testid="tribe-explore-neutral"
                onClick={() => { setTribe("neutral"); setStep(STEP_APPLIANCE); }}
                className="text-left p-6 rounded-2xl border-2 border-dashed border-edge hover:border-navy hover:-translate-y-0.5 transition-all"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-widest font-bold text-ink-soft">No tribe</div>
                    <div className="font-display font-bold text-navy text-xl mt-1">No tribe, thanks — skip this</div>
                  </div>
                  <ArrowRight className="text-ink-soft" />
                </div>
                <p className="mt-3 text-sm text-ink-mid leading-relaxed">
                  Same 12-session program, no manifesto or tribe theming. Certificates and graduation stay simple and neutral.
                </p>
              </button>
            </div>
            <div className="mt-8 flex justify-between">
              <button onClick={() => setStep(STEP_TRIBE_INTRO)} className="text-ink-mid px-4 py-3">Back</button>
              {tribe && tribe !== "neutral" && (
                <button data-testid="tribe-continue-with-chosen" onClick={() => setStep(STEP_INDUCTION_MANIFESTO)} className="bg-gold text-ink-dark px-6 py-3 rounded-full font-semibold hover:bg-gold-light">
                  Continue with {TRIBES[tribe].name}
                </button>
              )}
            </div>
          </div>
        )}

        {step === STEP_TRIBE_DETAIL && tribeExploring && (
          <div className="bg-warm-white border border-edge rounded-3xl overflow-hidden">
            <div className="p-8 md:p-12 relative" style={{ background: tribeExploring.key === "nasal_ninja" ? tribeExploring.bg : tribeExploring.surface, color: tribeExploring.ink }}>
              <button onClick={() => setStep(STEP_TRIBE_EXPLORE)} data-testid="tribe-detail-back" className="inline-flex items-center gap-2 text-sm opacity-70 hover:opacity-100"><ArrowLeft size={14} /> All tribes</button>
              <div className="mt-4 text-xs uppercase tracking-[0.3em] font-bold" style={{ color: tribeExploring.accent }}>Tribe</div>
              <h2 className="font-display text-4xl font-bold mt-2">{tribeExploring.name}</h2>
              <div className="mt-6" data-testid={`tribe-video-${tribeExploring.key}`}>
                <TribeVideo tribe={tribeExploring} />
              </div>
              <div className="mt-6">
                <div className="text-xs uppercase tracking-widest opacity-70 font-semibold" style={{ color: tribeExploring.accent }}>{tribeExploring.academy_name ? "About the " + tribeExploring.academy_name : "About this tribe"}</div>
                <p className="mt-2 leading-relaxed whitespace-pre-line" style={{ color: tribeExploring.ink }}>
                  <TribeText tribeKey={tribeExploring.key} field="welcome_text" fallback={tribeExploring.welcome_text} />
                </p>
              </div>
              <div className="mt-4 rounded-2xl p-4 italic text-sm opacity-90 border" style={{ borderColor: tribeExploring.accent, color: tribeExploring.ink }}>
                "<TribeText tribeKey={tribeExploring.key} field="manifesto" fallback={tribeExploring.manifesto} />"
              </div>
              <div className="mt-4 text-sm opacity-70" style={{ color: tribeExploring.ink }}>
                Graduation: <span className="font-semibold">{tribeExploring.graduation_name}</span>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <button onClick={() => setStep(STEP_TRIBE_EXPLORE)} className="px-5 py-2.5 rounded-full border font-semibold text-sm" style={{ borderColor: tribeExploring.accent, color: tribeExploring.ink }}>Explore other tribes</button>
                <button data-testid={`tribe-choose-${tribeExploring.key}`} onClick={() => { setTribe(tribeExploring.key); setStep(STEP_INDUCTION_MANIFESTO); }} className="px-6 py-2.5 rounded-full font-semibold text-sm" style={{ background: tribeExploring.accent, color: "#1E1E2E" }}>
                  Choose {tribeExploring.name}
                </button>
              </div>
            </div>
          </div>
        )}

        {step === STEP_INDUCTION_MANIFESTO && tribe && (
          <ManifestoStep
            tribe={TRIBES[tribe]}
            onBack={() => setStep(STEP_TRIBE_EXPLORE)}
            onContinue={() => setStep(STEP_INDUCTION_WELCOME)}
          />
        )}

        {step === STEP_INDUCTION_WELCOME && tribe && (
          <WelcomeStep
            tribe={TRIBES[tribe]}
            name={name}
            onContinue={() => setStep(STEP_APPLIANCE)}
          />
        )}

        {step === STEP_APPLIANCE && (
          <div className="bg-warm-white border border-edge rounded-3xl p-8 md:p-12">
            <h1 className="font-display text-3xl font-bold text-navy">Any orthodontic appliance?</h1>
            <p className="text-ink-mid mt-2">This helps us contextualize both the Lip Seal and Tongue Posture tracks on your dashboard — never hide progress, just add context.</p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              <button data-testid="appliance-yes" onClick={() => setAppliance(true)} className={`p-6 rounded-2xl border-2 font-semibold transition-all ${appliance === true ? "border-gold bg-cream text-navy" : "border-edge text-ink-mid hover:border-gold-light"}`}>Yes, expander or appliance</button>
              <button data-testid="appliance-no" onClick={() => setAppliance(false)} className={`p-6 rounded-2xl border-2 font-semibold transition-all ${appliance === false ? "border-gold bg-cream text-navy" : "border-edge text-ink-mid hover:border-gold-light"}`}>No, none currently</button>
            </div>
            <div className="mt-8 flex justify-between">
              <button onClick={() => setStep(isSelf ? STEP_TERMS : STEP_INDUCTION_WELCOME)} className="text-ink-mid px-4 py-3">Back</button>
              <button data-testid="enroll-appliance-next" disabled={appliance === null} onClick={() => setStep(STEP_SYMPTOMS)} className="bg-gold text-ink-dark px-6 py-3 rounded-full font-semibold hover:bg-gold-light disabled:opacity-50">Continue</button>
            </div>
          </div>
        )}

        {step === STEP_SYMPTOMS && (
          <div className="bg-warm-white border border-edge rounded-3xl p-8 md:p-12">
            <h1 className="font-display text-3xl font-bold text-navy">What are you seeing?</h1>
            <p className="text-ink-mid mt-2">Select every symptom you notice — no judgment. We'll offer you learn-more moments right after.</p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {SYMPTOM_OPTIONS.map((s) => {
                const on = symptoms.includes(s.key);
                return (
                  <button key={s.key} data-testid={`symptom-${s.key}`} onClick={() => toggleSymptom(s.key)} className={`text-left p-3 rounded-xl border transition-all text-sm ${on ? "border-mauve bg-mauve-pale text-navy font-semibold" : "border-edge text-ink-mid hover:border-mauve"}`}>
                    {on && <Check size={14} className="inline mr-1 text-mauve" />}{s.name}
                  </button>
                );
              })}
            </div>
            <div className="mt-8 flex justify-between">
              <button onClick={() => setStep(STEP_APPLIANCE)} className="text-ink-mid px-4 py-3">Back</button>
              <button data-testid="enroll-symptoms-next" onClick={() => { if (symptoms.length === 0) { createChild(); } else { setSymptomIdx(0); setSymptomStage("ask"); setStep(STEP_SYMPTOM_LEARN); } }} className="bg-gold text-ink-dark px-6 py-3 rounded-full font-semibold hover:bg-gold-light">Continue</button>
            </div>
          </div>
        )}

        {step === STEP_SYMPTOM_LEARN && activeSymptomData && (
          <div className="bg-warm-white border border-edge rounded-3xl p-8 md:p-12" data-testid="symptom-learn">
            <div className="text-xs uppercase tracking-widest text-mauve font-semibold">Symptom {symptomIdx + 1} of {symptoms.length}</div>
            <h1 className="font-display text-3xl font-bold text-navy mt-1">{activeSymptomData.name}</h1>

            {symptomStage === "ask" && !isMergedPalate && (
              <>
                <p className="text-ink-mid mt-4 leading-relaxed">
                  Want to learn more about <b>{activeSymptomData.name}</b> — the connection, severity levels, and when a referral might be recommended?
                </p>
                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                  <button data-testid="symptom-learn-yes" onClick={() => window.open(`/library/${currentSymptomKey}`, "_blank") && setSymptomStage("safety")} className="p-4 rounded-2xl border-2 border-gold bg-cream text-navy font-semibold hover:-translate-y-0.5 transition text-left">
                    <div className="flex items-center gap-2"><Eye size={16} /> Yes — open the full page</div>
                    <div className="text-xs text-ink-mid font-normal mt-1">Opens in a new tab so you don't lose your place.</div>
                  </button>
                  <button data-testid="symptom-learn-no" onClick={() => setSymptomStage("safety")} className="p-4 rounded-2xl border-2 border-edge text-ink-mid font-semibold hover:border-mauve transition text-left">
                    No — I'll come back to it later
                    <div className="text-xs text-ink-soft font-normal mt-1">We'll still show you a brief safety note before moving on.</div>
                  </button>
                </div>
              </>
            )}

            {symptomStage === "ask" && isMergedPalate && (
              <>
                <p className="text-ink-mid mt-4 leading-relaxed">
                  These two signs are closely linked — dive into either (or both) to learn more, then we'll continue with a shared baseline.
                </p>
                <div className="mt-6 grid sm:grid-cols-2 gap-3" data-testid="symptom-learn-merged">
                  <button data-testid="symptom-learn-crooked_teeth" onClick={() => window.open(`/library/crooked_teeth`, "_blank")} className="p-4 rounded-2xl border-2 border-gold bg-cream text-navy font-semibold hover:-translate-y-0.5 transition text-left">
                    <div className="flex items-center gap-2"><Eye size={16} /> Crooked/Crowded Teeth</div>
                    <div className="text-xs text-ink-mid font-normal mt-1">Symptom library page — tongue posture, palate shape, orthodontic notes. Opens in a new tab.</div>
                  </button>
                  <button data-testid="symptom-learn-narrow_palate" onClick={() => window.open(`/library/narrow_palate`, "_blank")} className="p-4 rounded-2xl border-2 border-gold bg-cream text-navy font-semibold hover:-translate-y-0.5 transition text-left">
                    <div className="flex items-center gap-2"><Eye size={16} /> Palate Size & How to Measure</div>
                    <div className="text-xs text-ink-mid font-normal mt-1">Includes the informational + measurement-walkthrough videos. Opens in a new tab.</div>
                  </button>
                </div>
                <div className="mt-4">
                  <button data-testid="symptom-learn-merged-continue" onClick={() => setSymptomStage("safety")} className="text-sm text-ink-mid underline hover:text-navy">
                    Skip / continue without opening either →
                  </button>
                </div>
              </>
            )}

            {symptomStage === "safety" && (
              <>
                <div className="mt-6 rounded-2xl bg-mauve-pale border border-mauve p-5" data-testid="symptom-safety-callout">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="text-mauve mt-0.5" size={20} />
                    <div>
                      <div className="font-semibold text-navy">Before we continue</div>
                      {isMergedPalate && mergedParts.length === 2 ? (
                        <div className="text-sm text-ink-dark mt-1 leading-relaxed space-y-2">
                          <div><span className="font-semibold text-navy">Crooked/Crowded Teeth:</span> {mergedParts[0].safety_note || mergedParts[0].description}</div>
                          <div><span className="font-semibold text-navy">Narrow Palate:</span> {mergedParts[1].safety_note || "A high, narrow palate is worth flagging to your dentist or orthodontist."}</div>
                        </div>
                      ) : (
                        <div className="text-sm text-ink-dark mt-1 leading-relaxed">{activeSymptomData.safety_note || activeSymptomData.referral || activeSymptomData.description}</div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-6 flex justify-between">
                  <button onClick={() => setSymptomStage("ask")} className="text-ink-mid px-4 py-3">Back</button>
                  <button data-testid="symptom-safety-continue" onClick={() => setSymptomStage("baseline")} className="bg-gold text-ink-dark px-6 py-3 rounded-full font-semibold hover:bg-gold-light">
                    Continue
                  </button>
                </div>
              </>
            )}

            {symptomStage === "baseline" && (
              <>
                <div className="mt-6" data-testid="symptom-baseline">
                  <label className="text-sm text-ink-dark font-semibold block">
                    Tell us about {activeSymptomData.name.toLowerCase()} right now
                  </label>
                  <p className="text-xs text-ink-mid mt-1">
                    How often, how severe, how it shows up — whatever helps us understand your starting point.
                  </p>
                  <textarea
                    data-testid={`symptom-baseline-${currentSymptomKey}`}
                    value={symptomBaselines[currentSymptomKey] || ""}
                    onChange={(e) => setSymptomBaselines((b) => ({ ...b, [currentSymptomKey]: e.target.value }))}
                    rows={4}
                    className="mt-3 w-full px-4 py-3 rounded-xl border border-edge bg-cream focus:outline-none focus:ring-2 focus:ring-gold text-ink-dark"
                    placeholder="e.g., 3-4 nights a week, loud enough to hear from the hallway…"
                  />
                </div>
                <div className="mt-6 flex justify-between">
                  <button onClick={() => setSymptomStage("safety")} className="text-ink-mid px-4 py-3">Back</button>
                  <button
                    data-testid="symptom-baseline-continue"
                    onClick={advanceSymptomLoop}
                    disabled={saving}
                    className="bg-gold text-ink-dark px-6 py-3 rounded-full font-semibold hover:bg-gold-light disabled:opacity-50"
                  >
                    {saving ? "Saving…" : (symptomIdx + 1 < symptoms.length ? "Next symptom" : "Continue")}
                  </button>
                </div>
              </>
            )}
            {error && <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
          </div>
        )}

        {step === STEP_BREATHE && child && (
          <div
            className="bg-warm-white border-2 border-gold rounded-3xl shadow-[0_20px_60px_rgba(44,45,91,0.12)] p-8 md:p-12"
            data-testid="breathe-intro-popup"
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-navy text-gold flex items-center justify-center">
                <Wind size={26} />
              </div>
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-widest text-mauve font-semibold">Foundational · required</div>
                <h1 className="font-display text-2xl md:text-3xl font-bold text-navy mt-1">
                  Everything we're doing follows the BREATHE Framework.
                </h1>
              </div>
            </div>

            <p className="mt-6 text-ink-dark leading-relaxed">
              This is what makes this program work. It isn't just exercises — it's seven pieces working together.
              If progress ever feels stuck, it's usually because one of these pieces needs more attention.
              Here's a quick look at each one:
            </p>

            <ul className="mt-6 space-y-3" data-testid="breathe-intro-list">
              {BREATHE_FRAMEWORK.map((item) => (
                <li key={item.key} className="flex items-start gap-3" data-testid={`breathe-intro-row-${item.key}`}>
                  <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-cream border border-gold text-navy font-display font-bold flex items-center justify-center">
                    {item.letter}
                  </span>
                  <span className="pt-1 text-sm md:text-base text-ink-dark leading-relaxed">
                    <span className="font-semibold text-navy">{item.letter} — {item.title}:</span>{" "}
                    <span className="text-ink-mid">{item.short}</span>
                  </span>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-wrap gap-3 justify-end">
              <button
                data-testid="breathe-intro-learn-more"
                onClick={() => {
                  window.open("/learn/breathe-framework", "_blank");
                  setStep(STEP_EDU_GOALS);
                }}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full border-2 border-navy text-navy font-semibold hover:bg-navy hover:text-warm-white transition"
              >
                Learn more
              </button>
              <button
                data-testid="breathe-intro-continue"
                onClick={() => setStep(STEP_EDU_GOALS)}
                className="bg-gold text-ink-dark px-6 py-3 rounded-full font-semibold hover:bg-gold-light hover:-translate-y-0.5 transition-all"
              >
                Got it, let's continue
              </button>
            </div>
          </div>
        )}

        {step === STEP_EDU_GOALS && child && (() => {
          const m = EDUCATION_MODULES[0]; // Goals & Terms
          const continueHandler = async () => {
            try { await api.post("/auth/education-complete"); await refreshUser?.(); } catch {}
            setStep(STEP_BASELINE_VIDEO);
          };
          const skipAll = async () => {
            setStep(STEP_BASELINE_VIDEO);
          };
          return (
            <div>
              {educationAlreadyCompleted && (
                <div className="mb-4 p-4 rounded-2xl bg-cream border border-gold flex items-start justify-between gap-4 flex-wrap" data-testid="edu-skip-banner">
                  <div className="text-sm text-ink-dark">
                    <span className="font-semibold text-navy">You've already completed this module.</span> Skip straight to the baseline check-in, or revisit anytime from the Learn menu.
                  </div>
                  <button data-testid="edu-skip-all" onClick={skipAll} className="inline-flex items-center gap-2 bg-navy text-warm-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-navy-deep">
                    <FastForward size={14} /> Skip module
                  </button>
                </div>
              )}
              <EducationModule
                number={1}
                total={1}
                title={m.title}
                subtitle={m.subtitle}
                body={m.body}
                moduleKey={m.key}
                viewed={eduViewed[m.key]}
                onMarkViewed={() => setEduViewed({ ...eduViewed, [m.key]: true })}
                onContinue={continueHandler}
                testidBase={`edu-${m.key}`}
              />
            </div>
          );
        })()}

        {step === STEP_BASELINE_VIDEO && child && (
          <BaselineVideoStep child={child} onNext={() => setStep(STEP_BASELINE_FORM)} />
        )}

        {step === STEP_BASELINE_FORM && child && (
          <div className="bg-warm-white border border-edge rounded-3xl p-8 md:p-12">
            <div className="mb-6">
              <div className="text-xs uppercase tracking-widest text-mauve font-semibold">Baseline Myo Check-In</div>
              <h1 className="font-display text-3xl font-bold text-navy mt-1">Where are we starting?</h1>
              <p className="text-ink-mid mt-2">Answer with today's honest snapshot. Skip anything you're unsure of — we'll remind you to come back.</p>
            </div>
            <CheckInForm
              childId={child.id}
              sessionNumber={1}
              activeSymptoms={(child.top_symptoms || []).filter((k) => !((child.dismissed_symptoms) || []).includes(k))}
              onDone={onBaselineDone}
              onDraftSaved={onBaselineDone}
            />
          </div>
        )}
      </div>
    </div>
  );
}
